import { useState, type ReactNode } from 'react'
import { Building2, CalendarPlus, ChevronDown, ChevronUp, ClipboardList, Hammer, LayoutList, ListChecks, Pencil, Send, SlidersHorizontal, UserPlus } from 'lucide-react'
import { Button, ButtonGlyph, buttonClass, EndpointHint, HelpIcon, isNotFoundError, Link, LoadFailure, Skeleton, type ButtonIcon } from '../../core/index.js'
import { useMonthHealth, useRemindAll, useSchedulePeriod, useSchedulePeriodWorkers } from '../hooks.js'
import type { HealthAction, HealthFinding, HealthLens, MonthHealthView } from '../types.js'
import { ACTION_LABEL, LENS_LABEL, SEVERITY_DOT, SEVERITY_LABEL, VERDICT_CLASS, VERDICT_LABEL, describeDates, lensTiles, monthLabel, monthRange, sortFindings, type LensTile } from './monthHealthData.js'
import { MonthStepper } from './MonthStepper.js'
import { RemindAllButton } from './RemindAllButton.js'
import { periodStatusChip } from './scheduleRunText.js'
import { PublishMissingSeats } from './PublishMissingSeats.js'
import type { ShiftTypeKey } from '../../facilities/index.js'

const SHIFT_TYPE_LABEL: Record<string, string> = { day: 'Day', evening: 'Evening', night: 'Night' }

/** The icon each remedy's button leads with. */
const ACTION_ICON: Record<HealthAction, ButtonIcon> = {
  record_census: ClipboardList,
  configure_units: Building2,
  set_staffing_plan: SlidersHorizontal,
  publish_shifts: CalendarPlus,
  add_worker: UserPlus,
  edit_worker: Pencil,
  remind_all: Send,
  build_month: Hammer,
  assign_seats: ListChecks,
  open_board: LayoutList,
  release: Send,
}

const isShiftTypeKey = (value: string | null): value is ShiftTypeKey => value === 'day' || value === 'evening' || value === 'night'

/**
 * Where each remedy that is a screen lives: a part of the facility's Dashboard (the Today tab, or the Month tab on
 * the sub-tab that fixes it - facility operations dashboard plan §3) or a Configuration tab, opened by its link.
 */
function actionHref(action: HealthAction, hrefs: { dashboard: string; config: string }): string | null {
  switch (action) {
    case 'record_census':
      return `${hrefs.dashboard}?tab=today`
    case 'open_board':
      return `${hrefs.dashboard}?tab=month&detail=board`
    case 'publish_shifts':
      return `${hrefs.dashboard}?tab=month&detail=publish`
    case 'build_month':
    case 'release':
      return `${hrefs.dashboard}?tab=month&detail=build`
    case 'assign_seats':
      return `${hrefs.dashboard}?tab=month&detail=unfilled`
    case 'configure_units':
      return `${hrefs.config}?tab=details`
    case 'set_staffing_plan':
      return `${hrefs.config}?tab=scheduling&sub=census`
    default:
      return null
  }
}

/** The status, the verdict and a tile per lens; the tiles open the lens's findings below where `onOpen` is given, and the arrows step the month where `onStep` is. */
export function MonthHealthSummary({ view, activeLens = null, onOpen, onStep, range }: { view: MonthHealthView; activeLens?: HealthLens | null; onOpen?: (lens: HealthLens | null) => void; /** Steps to the month chosen; left out, the month is fixed. */ onStep?: (period: string) => void; /** The months the arrows step between. */ range?: { first: string; last: string } }) {
  const tiles = lensTiles(view)
  const statusChip = periodStatusChip(view.status, view.counts.bidding)
  const tile = (entry: LensTile): ReactNode => {
    const active = activeLens === entry.lens
    const body = (
      <>
        <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
          {entry.worst && entry.worst !== 'info' && <span className={`h-2 w-2 rounded-full ${SEVERITY_DOT[entry.worst]}`} title={SEVERITY_LABEL[entry.worst]} aria-label={SEVERITY_LABEL[entry.worst]} />}
          {entry.label}
        </div>
        <div className="text-xl font-black leading-tight text-ink">{entry.count === 0 ? 'OK' : entry.count}</div>
        <div className={`text-[10px] leading-snug ${entry.worst === 'block' ? 'font-bold text-coral' : entry.worst === 'warn' ? 'font-bold text-ink' : 'text-ink-mid'}`}>{entry.count === 0 ? 'Nothing to do' : entry.headline}</div>
        {active && (
          <div className="mt-1 flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-marine">
            Showing below <ChevronDown className="h-3 w-3" aria-hidden />
          </div>
        )}
      </>
    )
    const frame = `rounded border bg-white p-2.5 text-left ${active ? 'border-marine ring-1 ring-marine' : 'border-marine/15'}`
    if (!onOpen) {
      return (
        <div key={entry.lens} className={frame}>
          {body}
        </div>
      )
    }
    return (
      <button key={entry.lens} type="button" aria-pressed={active} onClick={() => onOpen(active ? null : entry.lens)} className={`${frame} transition-colors hover:border-marine`}>
        {body}
      </button>
    )
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px]">
        {/* The arrows sit either side of the month's name, so stepping reads as turning a page. The month is the card's
            largest text: every count and finding here is about it (the user's call, 22 October 2026). */}
        <span className="inline-flex items-center gap-1.5">
          {onStep ? <MonthStepper size="lg" value={view.period} onChange={onStep} min={range?.first} max={range?.last} /> : <span className="text-2xl font-black leading-none text-ink sm:text-3xl">{monthLabel(view.period)}</span>}
        </span>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${statusChip.className}`}>{statusChip.label}</span>
          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${VERDICT_CLASS[view.verdict]}`}>{VERDICT_LABEL[view.verdict]}</span>
          <span className="text-ink-mid">
            {view.counts.shiftsPublished} published
            {view.basis !== 'none' && ` · ${view.counts.unfilled} unfilled`}
            {view.counts.seatsRequired > 0 && ` · ${view.counts.seatsRequired} required by the plan`}
            {view.basis !== 'none' && view.counts.seatsRequired > 0 && ` · ${view.counts.seatsShort} short of it · ${view.counts.seatsSurplus} over it`}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">{tiles.map(tile)}</div>
    </div>
  )
}

/** The unit, shift type, role and days a finding is about, as small chips. */
function Where({ finding, view }: { finding: HealthFinding; view: MonthHealthView }) {
  const [showDates, setShowDates] = useState(false)
  const unit = finding.unitId ? (view.byUnit.find((entry) => entry.unitId === finding.unitId)?.name ?? finding.unitId) : null
  const chips = [unit, finding.shiftType ? SHIFT_TYPE_LABEL[finding.shiftType] : null, finding.role].filter((chip): chip is string => chip !== null)
  if (chips.length === 0 && finding.dates.length === 0) {
    return null
  }
  return (
    <div className="flex flex-wrap items-center gap-1 text-[10px] text-ink-mid">
      {chips.map((chip) => (
        <span key={chip} className="rounded border border-marine/15 bg-canvas px-1.5 py-0.5">
          {chip}
        </span>
      ))}
      {finding.dates.length > 0 && (
        <button type="button" className="inline-flex items-center gap-0.5 text-marine hover:underline" onClick={() => setShowDates((current) => !current)} aria-expanded={showDates}>
          {describeDates(finding.dates)}
          {showDates ? <ChevronUp className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}
        </button>
      )}
      {showDates && <span className="w-full text-ink-mid/80">{finding.dates.map((date) => date.slice(8, 10).replace(/^0/, '')).join(', ')}</span>}
    </div>
  )
}

/** One finding: its mark, its words, where, and the remedy at the right. */
function FindingRow({ finding, view, action }: { finding: HealthFinding; view: MonthHealthView; action: ReactNode }) {
  return (
    <li className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 gap-2">
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[finding.severity]}`} title={SEVERITY_LABEL[finding.severity]} aria-label={SEVERITY_LABEL[finding.severity]} />
        <div className="flex min-w-0 flex-col gap-1">
          <div className={`text-xs ${finding.severity === 'block' ? 'font-bold text-coral' : finding.severity === 'warn' ? 'font-bold text-ink' : 'text-ink-mid'}`}>{finding.message}</div>
          <Where finding={finding} view={view} />
        </div>
      </div>
      {action && <div className="shrink-0 sm:pl-3">{action}</div>}
    </li>
  )
}

/** How many findings are shown before the rest fold behind Show all: a month short on every line is long. */
const FINDINGS_SHOWN = 8

/** The findings of one lens, or every lens, blocks first; `renderAction` gives each its button, or nothing for a read-only view. */
export function MonthHealthFindings({ view, lens = null, renderAction }: { view: MonthHealthView; lens?: HealthLens | null; renderAction?: (finding: HealthFinding) => ReactNode }) {
  const [showAll, setShowAll] = useState(false)
  const sorted = sortFindings(lens ? view.findings.filter((entry) => entry.lens === lens) : view.findings)
  if (sorted.length === 0) {
    return <p className="text-xs text-ink-mid">{lens ? `Nothing to do about the ${LENS_LABEL[lens].toLowerCase()}.` : 'Nothing to do: the month is ready.'}</p>
  }
  const shown = showAll ? sorted : sorted.slice(0, FINDINGS_SHOWN)
  const folded = sorted.length - shown.length
  return (
    <div>
      <ul className="divide-y divide-marine/10">{shown.map((finding, index) => <FindingRow key={`${finding.key}-${finding.unitId ?? ''}-${finding.shiftType ?? ''}-${finding.role ?? ''}-${index}`} finding={finding} view={view} action={renderAction?.(finding) ?? null} />)}</ul>
      {(folded > 0 || showAll) && (
        <button type="button" className="mt-1 text-[11px] font-bold text-marine hover:underline" onClick={() => setShowAll((current) => !current)}>
          {showAll ? 'Show fewer' : `Show ${folded} more`}
        </button>
      )}
    </div>
  )
}

/** The month as the admin's dialog and the Partner Simulator show it: the summary and the findings, no buttons. */
export function MonthHealthReadOnly({ view }: { view: MonthHealthView }) {
  const [lens, setLens] = useState<HealthLens | null>(null)
  return (
    <div className="flex flex-col gap-3">
      <MonthHealthSummary view={view} activeLens={lens} onOpen={setLens} />
      <MonthHealthFindings view={view} lens={lens} />
    </div>
  )
}

interface MonthHealthCardProps {
  facilityId: string
  /** Where this facility's Tools and Configuration are, for the buttons that open a screen. */
  dashboardHref: string
  configHref: string
  /** Opens the New worker form in place. */
  onAddWorker?: () => void
  /** Opens a worker's roster row in place, for a credential to fix. */
  onEditWorker?: (workerId: string) => void
  /** The roster's names, for the workers a finding names. */
  names?: ReadonlyMap<string, string>
}

/**
 * The Month card on the facility dashboard (month health plan §3): the
 * month's status and verdict, a tile per lens - the census, the workers,
 * the units - and, under the tile opened, each finding with the coral button
 * that remedies it: a screen opened on the right tab, Remind all in place,
 * the New worker form, a worker's row. A finding that is information keeps
 * its button too but never colours the verdict. Polled every minute.
 */
export function MonthHealthCard({ facilityId, dashboardHref, configHref, onAddWorker, onEditWorker, names }: MonthHealthCardProps) {
  const [period, setPeriod] = useState<string | null>(null)
  const [lens, setLens] = useState<HealthLens | null>(null)
  const health = useMonthHealth(facilityId, period)
  const shownPeriod = health.data?.period ?? period
  // The arrows step back to the facility's first month with a run or a published shift and on to the second month ahead; the card opens on the one being built next.
  const firstPeriod = useSchedulePeriod(facilityId, shownPeriod).data?.firstPeriod ?? null
  const range = monthRange(new Date(), firstPeriod)
  // Remind all needs who is due, which the readiness read holds; read only once the card has something to remind about.
  const needsReminders = health.data?.findings.some((finding) => finding.action === 'remind_all') ?? false
  const workers = useSchedulePeriodWorkers(facilityId, shownPeriod, needsReminders)
  const remindAll = useRemindAll(facilityId, shownPeriod)
  const hrefs = { dashboard: dashboardHref, config: configHref }

  function renderAction(finding: HealthFinding): ReactNode {
    const action = finding.action
    if (!action) {
      return null
    }
    if (action === 'remind_all') {
      return workers.data ? <RemindAllButton workers={workers.data.workers} onRemindAll={() => remindAll.mutateAsync()} /> : <span className="text-[10px] text-ink-mid">Loading who to remind…</span>
    }
    if (action === 'add_worker') {
      return onAddWorker ? (
        <Button icon={UserPlus} size="sm" onClick={onAddWorker}>
          {ACTION_LABEL.add_worker}
        </Button>
      ) : null
    }
    if (action === 'edit_worker') {
      return onEditWorker ? (
        <div className="flex flex-col items-start gap-1">
          {finding.workerIds.map((workerId) => (
            <Button key={workerId} icon={Pencil} size="sm" variant="secondary" onClick={() => onEditWorker(workerId)}>
              {names?.get(workerId) ?? workerId}
            </Button>
          ))}
        </div>
      ) : null
    }
    // A seat never published, on a unit and shift type the finding names: published in place, not through the form.
    if (action === 'publish_shifts' && finding.seatsByDate && finding.unitId && isShiftTypeKey(finding.shiftType)) {
      return <PublishMissingSeats facilityId={facilityId} missing={{ unitId: finding.unitId, shiftType: finding.shiftType, role: finding.role, seatsByDate: finding.seatsByDate }} />
    }
    const href = actionHref(action, hrefs)
    if (!href) {
      return null
    }
    return (
      <Link to={href} className={buttonClass({ size: 'sm', variant: finding.severity === 'info' ? 'secondary' : 'primary' })}>
        <ButtonGlyph icon={ACTION_ICON[action]} size="sm" />
        {ACTION_LABEL[action]}
      </Link>
    )
  }

  return (
    <section aria-label="The month">
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-marine/15 pb-2 text-[10px] font-bold uppercase tracking-wide text-marine">
        <span className="inline-flex items-center gap-1">
          The month
          <HelpIcon topic="schedule.month-health" />
          <EndpointHint className="ml-2">GET /facilities/{'{facilityId}'}/schedule/health</EndpointHint>
        </span>
      </div>
      {health.isPending ? (
        <Skeleton label="Loading the month" />
      ) : health.isError ? (
        isNotFoundError(health.error) ? null : (
          <LoadFailure size="xs" message="The month could not be read." onRetry={() => void health.refetch()} retrying={health.isFetching} />
        )
      ) : (
        <div className="flex flex-col gap-3">
          <MonthHealthSummary view={health.data} activeLens={lens} onOpen={setLens} onStep={setPeriod} range={range} />
          <MonthHealthFindings view={health.data} lens={lens} renderAction={renderAction} />
        </div>
      )}
    </section>
  )
}

interface HealthFixLinkProps {
  action: HealthAction | null
  dashboardHref: string
  configHref: string
  /** Opens the New worker form in place, for Add a worker. */
  onAddWorker?: () => void
}

/** A finding's remedy as one small button, where it is a screen or the New worker form: for a finding shown away from the Month card. */
export function HealthFixLink({ action, dashboardHref, configHref, onAddWorker }: HealthFixLinkProps) {
  if (!action) {
    return null
  }
  if (action === 'add_worker') {
    return onAddWorker ? (
      <Button icon={UserPlus} size="sm" variant="secondary" onClick={onAddWorker}>
        {ACTION_LABEL.add_worker}
      </Button>
    ) : null
  }
  const href = actionHref(action, { dashboard: dashboardHref, config: configHref })
  if (!href) {
    return null
  }
  return (
    <Link to={href} className={buttonClass({ size: 'sm', variant: 'secondary' })}>
      <ButtonGlyph icon={ACTION_ICON[action]} size="sm" />
      {ACTION_LABEL[action]}
    </Link>
  )
}
